import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import withAuth from '../../auth/WithAuth';
import './projectmanagement.scss';
import FurnitureNavbar from '../../../components/layout/Nav';
import { serverUrlAuth } from '../../../serverUrl';

const ProjectManagement = () => {
  const [projectsData, setProjectsData] = useState({
    total_projects: 0,
    open_projects: 0,
    active_projects: 0,
    completed_projects: 0,
    projects: [],
  });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    category: 'all',
    budgetRange: 'all',
  });
  // Add state for carousel per project
  const [currentImageIndices, setCurrentImageIndices] = useState({});

  const fetchProjectManagementData = async () => {
    try {
      const response = await serverUrlAuth.get(
        'projects/admin/project-management/'
      );
      setProjectsData(response.data);
      // Initialize carousel indices for each project
      const initialIndices = {};
      response.data.projects.forEach((project) => {
        if (project.images && project.images.length > 0) {
          initialIndices[project.id] = 0;
        }
      });
      setCurrentImageIndices(initialIndices);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectManagementData();
  }, []);

  // Carousel navigation functions
  const nextImage = (projectId) => {
    const project = projectsData.projects.find((p) => p.id === projectId);
    if (!project || !project.images || project.images.length <= 1) return;

    setCurrentImageIndices((prev) => ({
      ...prev,
      [projectId]: (prev[projectId] + 1) % project.images.length,
    }));
  };

  const prevImage = (projectId) => {
    const project = projectsData.projects.find((p) => p.id === projectId);
    if (!project || !project.images || project.images.length <= 1) return;

    setCurrentImageIndices((prev) => ({
      ...prev,
      [projectId]:
        (prev[projectId] - 1 + project.images.length) % project.images.length,
    }));
  };

  const setImageIndex = (projectId, index) => {
    setCurrentImageIndices((prev) => ({
      ...prev,
      [projectId]: index,
    }));
  };

  const filteredProjects = projectsData.projects.filter((project) => {
    if (filters.status !== 'all' && project.status !== filters.status)
      return false;
    if (filters.category !== 'all' && project.category !== filters.category)
      return false;

    if (filters.budgetRange !== 'all') {
      const budget = project.budget;
      switch (filters.budgetRange) {
        case 'low':
          if (budget > 20000) return false;
          break;
        case 'medium':
          if (budget <= 20000 || budget > 50000) return false;
          break;
        case 'high':
          if (budget <= 50000) return false;
          break;
        // default
      }
    }

    if (
      filters.search &&
      !project.title.toLowerCase().includes(filters.search.toLowerCase()) &&
      !project.client.toLowerCase().includes(filters.search.toLowerCase()) &&
      !project.description.toLowerCase().includes(filters.search.toLowerCase())
    )
      return false;

    return true;
  });

  const getStatusCount = (status) =>
    projectsData.projects.filter((p) => p.status === status).length;

  const getStatusInfo = (status) => {
    const info = {
      open: { color: '#f59e0b', icon: '🔓', label: 'Open' },
      'in-progress': { color: '#3b82f6', icon: '⚡', label: 'In Progress' },
      completed: { color: '#10b981', icon: '✅', label: 'Completed' },
      cancelled: { color: '#ef4444', icon: '❌', label: 'Cancelled' },
    };
    return info[status] || { color: '#6b7280', icon: '❓', label: 'Unknown' };
  };

  const handleStatusUpdate = (projectId, newStatus) => {
    const updatedProjects = projectsData.projects.map((p) =>
      p.id === projectId ? { ...p, status: newStatus } : p
    );
    setProjectsData({ ...projectsData, projects: updatedProjects });
  };

  const handleBulkAction = (action) => {
    alert(`Bulk action: ${action} selected`);
  };

  return (
    <>
      <FurnitureNavbar />
      <div className="admin-projects">
        {/* Page Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-text">
              <h1>📋 Projects Management</h1>
              <p>Monitor and manage all projects on the platform</p>
            </div>
            <div className="header-actions">
              <button
                className="btn btn-primary"
                onClick={() => handleBulkAction('export')}
              >
                📊 Export Report
              </button>
            </div>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="stats-overview">
          <div className="stat-card total-projects">
            <div className="stat-content">
              <div className="stat-icon">📋</div>
              <div className="stat-info">
                <h3>Total Projects</h3>
                <p className="stat-value">{projectsData.total_projects}</p>
              </div>
            </div>
          </div>

          <div className="stat-card open-projects">
            <div className="stat-content">
              <div className="stat-icon">🔓</div>
              <div className="stat-info">
                <h3>Open</h3>
                <p className="stat-value">{projectsData.open_projects}</p>
                <span className="stat-change neutral">Seeking artisans</span>
              </div>
            </div>
          </div>

          <div className="stat-card progress-projects">
            <div className="stat-content">
              <div className="stat-icon">⚡</div>
              <div className="stat-info">
                <h3>In Progress</h3>
                <p className="stat-value">{projectsData.active_projects}</p>
                <span className="stat-change positive">Active work</span>
              </div>
            </div>
          </div>

          <div className="stat-card completed-projects">
            <div className="stat-content">
              <div className="stat-icon">✅</div>
              <div className="stat-info">
                <h3>Completed</h3>
                <p className="stat-value">{projectsData.completed_projects}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters-bar">
            <div className="search-box">
              <input
                type="text"
                placeholder="Search projects by title, client, or description..."
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
              />
              <span className="search-icon">🔍</span>
            </div>

            <div className="filter-group">
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <select
                value={filters.category}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, category: e.target.value }))
                }
              >
                <option value="all">All Categories</option>
                <option value="furniture">Furniture</option>
                <option value="carpentry">Carpentry</option>
                <option value="metalwork">Metalwork</option>
              </select>

              <select
                value={filters.budgetRange}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    budgetRange: e.target.value,
                  }))
                }
              >
                <option value="all">All Budgets</option>
                <option value="low">Low (&lt; 20K)</option>
                <option value="medium">Medium (20K-50K)</option>
                <option value="high">High (&gt; 50K)</option>
              </select>
            </div>

            <div className="filter-actions">
              <button
                className="btn btn-secondary"
                onClick={() =>
                  setFilters({
                    status: 'all',
                    search: '',
                    category: 'all',
                    budgetRange: 'all',
                  })
                }
              >
                Clear Filters
              </button>
              <span className="results-count">
                {filteredProjects.length} of {projectsData.projects.length}{' '}
                projects
              </span>
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="projects-section">
          <div className="section-header">
            <h3>All Projects</h3>
            <div className="section-actions">
              <select
                className="bulk-actions"
                onChange={(e) => handleBulkAction(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>
                  Bulk Actions
                </option>
                <option value="archive">Archive Selected</option>
                <option value="export">Export Selected</option>
                <option value="notify">Notify Clients</option>
              </select>
            </div>
          </div>

          <div className="projects-grid">
            {filteredProjects.map((project) => {
              const statusInfo = getStatusInfo(project.status);
              const currentImageIndex = currentImageIndices[project.id] || 0;

              return (
                <div
                  key={project.id}
                  className={`project-card ${project.status}`}
                >
                  <div className="project-header">
                    <div className="project-title-section">
                      <h3>{project.title}</h3>
                    </div>
                    <div className="project-status">
                      <span
                        className="status-badge"
                        style={{ backgroundColor: statusInfo.color }}
                      >
                        <span className="status-icon">{statusInfo.icon}</span>
                        {statusInfo.label}
                      </span>
                      {project.artisan_name && (
                        <span className="assigned-artisan">
                          👤 {project.artisan_name}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Image Carousel */}
                  {project.images && project.images.length > 0 && (
                    <div className="image-carousel">
                      <div className="carousel-container">
                        <img
                          src={project.images[currentImageIndex]?.image}
                          alt={`Project preview ${currentImageIndex + 1}`}
                          className="main-image"
                          onClick={() =>
                            window.open(
                              project.images[currentImageIndex]?.image,
                              '_blank'
                            )
                          }
                        />
                        {project.images.length > 1 && (
                          <>
                            <button
                              className="carousel-btn prev"
                              onClick={() => prevImage(project.id)}
                              aria-label="Previous image"
                            >
                              ‹
                            </button>
                            <button
                              className="carousel-btn next"
                              onClick={() => nextImage(project.id)}
                              aria-label="Next image"
                            >
                              ›
                            </button>
                            <div className="carousel-dots">
                              {project.images.map((_, idx) => (
                                <button
                                  key={idx}
                                  className={`dot ${
                                    currentImageIndex === idx ? 'active' : ''
                                  }`}
                                  onClick={() => setImageIndex(project.id, idx)}
                                  aria-label={`Go to image ${idx + 1}`}
                                />
                              ))}
                            </div>
                            <div className="image-counter">
                              {currentImageIndex + 1} / {project.images.length}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="project-description">
                    <p>{project.description}</p>
                  </div>

                  <div className="project-details">
                    <div className="detail-row">
                      <div className="detail-item">
                        <span className="label">👤 Client</span>
                        <span className="value">{project.client}</span>
                      </div>
                      <div className="detail-item">
                        <span className="label">📍 Location</span>
                        <span className="value">{project.location}</span>
                      </div>
                    </div>
                    <div className="detail-row">
                      <div className="detail-item">
                        <span className="label">💰 Budget</span>
                        <span className="value budget">
                          KSh {project.budget?.toLocaleString()}
                        </span>
                      </div>
                      {project.agreed_amount && (
                        <div className="detail-item">
                          <span className="label">💰 Agreed Amount</span>
                          <span className="value agreed-amount">
                            KSH {project.agreed_amount?.toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="detail-row">
                      <div className="detail-item">
                        <span className="label">📆 Created</span>
                        <span className="value">
                          {new Date(project.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="detail-item">
                        <span className="label">📅 Deadline</span>
                        <span className="value deadline">
                          {new Date(project.deadline).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    <div className="detail-row">
                      <div className="detail-item">
                        <span className="label">📨 Proposals</span>
                        <span className="value proposals">
                          {project.proposal_count}
                        </span>
                      </div>
                      {project.status === 'in-progress' && (
                        <div className="detail-item">
                          <span className="label">👤 Assigned Artisan</span>
                          <span className="value">{project.artisan}</span>
                        </div>
                      )}
                      {/* <div className="detail-item">
                        <span className="label">📆 Created</span>
                        <span className="value">
                          {new Date(project.created_at).toLocaleDateString()}
                        </span>
                      </div> */}
                    </div>
                  </div>

                  {project.status === 'in-progress' && project.progress && (
                    <div className="progress-section">
                      <div className="progress-header">
                        <span>Progress</span>
                        <span className="progress-percent">
                          {project.progress}%
                        </span>
                      </div>
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{
                            width: `${project.progress}%`,
                            backgroundColor: statusInfo.color,
                          }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {/* <div className="project-actions">
                    <div className="primary-actions">
                      <Link to={`/admin/projects/${project.id}`} className="btn btn-outline view-btn">
                        👁️ View Details
                      </Link>
                      <button className="btn btn-icon" title="Message Client">
                        💬
                      </button>
                    </div>
                    <div className="secondary-actions">
                      <select
                        value={project.status}
                        onChange={(e) => handleStatusUpdate(project.id, e.target.value)}
                        className="status-select"
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button className="btn btn-icon more-actions" title="More actions">
                        ⋮
                      </button>
                    </div>
                  </div> */}
                </div>
              );
            })}
          </div>

          {filteredProjects.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No projects found</h3>
              <p>
                No projects match your current filter criteria. Try adjusting
                your search or filters.
              </p>
              <button
                className="btn btn-primary"
                onClick={() =>
                  setFilters({
                    status: 'all',
                    search: '',
                    category: 'all',
                    budgetRange: 'all',
                  })
                }
              >
                Clear All Filters
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="section-footer">
          <div className="footer-info">
            Showing {filteredProjects.length} of {projectsData.projects.length}{' '}
            projects
          </div>
          <div className="pagination">
            <button className="page-btn" disabled>
              ← Previous
            </button>
            <span className="page-info">Page 1 of 1</span>
            <button className="page-btn" disabled>
              Next →
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default withAuth(ProjectManagement, 'admin');
